import { loadCurrentState } from '../../storage';
import { calculateSectionStartTime } from './sectionStartTimes';
import checklistsDataDefault from '../../data/checklistsData.json';
import OpeningConditions from '../../OpeningConditions';

export interface ChecklistItem {
  key: string;
  label: string;
  checked: boolean;
  timestamp?: string;
  category?: string;
}

export interface ChecklistSection {
  items: ChecklistItem[];
  startTime: string | null;
}

export interface ApgarScores {
  pulse?: { min1?: number; min5?: number; min10?: number };
  breathing?: { min1?: number; min5?: number; min10?: number };
  muscleTone?: { min1?: number; min5?: number; min10?: number };
  skinColor?: { min1?: number; min5?: number; min10?: number };
  touchResponse?: { min1?: number; min5?: number; min10?: number };
}

export interface PDFData {
  motherName: string;
  creationDate: Date;

  // Opening conditions
  openingConditions: {
    anamnesis?: string;
    birthReason?: string;
    gestationalAgeWeeks?: number | null;
    gestationalAgeDays?: number | null;
    weightAssessment?: number | null;
    birthWeight?: number | null;
    gender?: 'זכר' | 'נקבה' | null;
    admissionTime?: string | null;
  };

  // Checklist sections
  sections: {
    medications: ChecklistSection;
    roomEquipment: ChecklistSection;
    ventilationEquipment: ChecklistSection;
    intubationEquipment: ChecklistSection;
    departmentAdmission: ChecklistSection;
    parents: ChecklistSection;
  };

  // Umbilical cord
  umbilicalCord: {
    option: 'one_minute' | 'less_than_minute' | 'not_performed' | null;
    reason: string;
  };

  // Apgar data
  apgar: {
    scores: ApgarScores;
    timerStartTime: number | null;
  };
}

type SectionKey = 'medications' | 'roomEquipment' | 'ventilationEquipment' | 'intubationEquipment' | 'departmentAdmission' | 'parents';

interface ChecklistConfig {
  key: string;
  label: string;
  category?: string;
}

/**
 * Load a checklist section from state and config
 */
function loadSection(
  sectionKey: SectionKey,
  checklistsConfig?: Record<string, ChecklistConfig[]>
): ChecklistSection {
  const state = loadCurrentState();
  if (!state) {
    return { items: [], startTime: null };
  }

  const booleanValues = (state[sectionKey] as Record<string, boolean>) || {};
  const timestampKey = `${sectionKey}Timestamps` as keyof typeof state;
  const timestamps = (state[timestampKey] as Record<string, string | undefined>) || {};

  // Use provided config or fall back to default
  const config = checklistsConfig?.[sectionKey] || (checklistsDataDefault as any)[sectionKey] || [];

  const items: ChecklistItem[] = config.map((configItem: ChecklistConfig) => ({
    key: configItem.key,
    label: configItem.label,
    category: configItem.category,
    checked: booleanValues[configItem.key] || false,
    timestamp: timestamps[configItem.key]
  }));

  // Calculate start time (earliest timestamp among checked items)
  const startTime = calculateSectionStartTime(timestamps);

  return { items, startTime };
}

/**
 * Calculate total Apgar score for a specific timepoint
 */
function calculateApgarTotal(scores: ApgarScores, timepoint: 'min1' | 'min5' | 'min10'): number | null {
  const values = [
    scores.pulse?.[timepoint],
    scores.breathing?.[timepoint],
    scores.muscleTone?.[timepoint],
    scores.skinColor?.[timepoint],
    scores.touchResponse?.[timepoint]
  ];

  // If any value is undefined, return null (incomplete)
  if (values.some(v => v === undefined || v === null)) {
    return null;
  }

  return values.reduce((sum, val) => sum + (val || 0), 0);
}

const PRENATAL_STEROIDS_OPTION_LABELS: Record<string, string> = {
  one: 'קורס אחד',
  two: 'שני קורסים',
};

const ANTIBIOTICS_OPTION_LABELS: Record<string, string> = {
  gbs_under4h: 'כיסוי ל-GBS פחות מ-4 שעות',
  gbs_over4h: 'כיסוי ל-GBS מעל 4 שעות',
  broad_under4h: 'כיסוי רחב טווח פחות מ-4 שעות',
  broad_over4h: 'כיסוי רחב טווח מעל 4 שעות',
  none: 'ללא טיפול אנטיביוטי',
};

function enrichMedicationsSection(section: ChecklistSection, state: NonNullable<ReturnType<typeof loadCurrentState>>): ChecklistSection {
  return {
    ...section,
    items: section.items.map((item) => {
      if (item.key === 'prenatalSteroids' && state.medications?.prenatalSteroidsOption) {
        const optionLabel = PRENATAL_STEROIDS_OPTION_LABELS[state.medications.prenatalSteroidsOption];
        return optionLabel ? { ...item, label: `${item.label} - ${optionLabel}` } : item;
      }
      if (item.key === 'maternalAntibiotics' && state.medications?.maternalAntibioticsOption) {
        const optionLabel = ANTIBIOTICS_OPTION_LABELS[state.medications.maternalAntibioticsOption];
        return optionLabel ? { ...item, label: `${item.label} - ${optionLabel}` } : item;
      }
      return item;
    }),
  };
}

/**
 * Load and transform NewPreemie state into PDF-ready data structure
 */
export const loadPDFData = (
  motherName: string,
  checklistsConfig?: Record<string, ChecklistConfig[]>
): PDFData => {
  const state = loadCurrentState();

  if (!state) {
    // Return empty data structure if no state exists
    return {
      motherName,
      creationDate: new Date(),
      openingConditions: {},
      umbilicalCord: { option: null, reason: '' },
      sections: {
        medications: { items: [], startTime: null },
        roomEquipment: { items: [], startTime: null },
        ventilationEquipment: { items: [], startTime: null },
        intubationEquipment: { items: [], startTime: null },
        departmentAdmission: { items: [], startTime: null },
        parents: { items: [], startTime: null }
      },
      apgar: {
        scores: {},
        timerStartTime: null
      }
    };
  }
  return {
    motherName,
    creationDate: new Date(),
    openingConditions: {
      anamnesis: state.anamnesis,
      birthReason: state.birthReason,
      gestationalAgeWeeks: state.gestationalAgeWeeks,
      gestationalAgeDays: state.gestationalAgeDays,
      weightAssessment: state.weightAssessment,
      birthWeight: state.departmentAdmission?.birthWeight ?? null,
      gender: state.gender,
      admissionTime: state.departmentAdmission?.admissionTime ?? null
    },
    umbilicalCord: {
      option: state.umbilicalCord?.option ?? null,
      reason: state.umbilicalCord?.reason ?? ''
    },
    sections: {
      medications: enrichMedicationsSection(loadSection('medications', checklistsConfig), state),
      roomEquipment: loadSection('roomEquipment', checklistsConfig),
      ventilationEquipment: loadSection('ventilationEquipment', checklistsConfig),
      intubationEquipment: loadSection('intubationEquipment', checklistsConfig),
      departmentAdmission: loadSection('departmentAdmission', checklistsConfig),
      parents: loadSection('parents', checklistsConfig)
    },
    apgar: {
      scores: state.apgar || {},
      timerStartTime: state.apgarTimer?.startTime || null
    }
  };
};

/**
 * Export helper function to calculate Apgar totals
 */
export const getApgarTotals = (scores: ApgarScores) => ({
  min1: calculateApgarTotal(scores, 'min1'),
  min5: calculateApgarTotal(scores, 'min5'),
  min10: calculateApgarTotal(scores, 'min10')
});
